/**
 * Yangiliklar bo'limi (ЧТЗ 4.2.11.1) — avtorizatsiyasiz:
 *
 *   * `/yangiliklar` — to'liq ro'yxat: sarlavha/kalit so'z bo'yicha qidiruv,
 *     toifa va sana filtri, «Yana yuklash» (arxiv), yuklanish/bo'sh/xato holatlari.
 *     Filtrlar manzil satrida saqlanadi — havolani ulashish mumkin.
 *   * `/yangiliklar/:id` — maqola: sarlavha, nashr sanasi, to'liq matn, suratlar,
 *     manba («Manba: …» va «Rasmiy manbada ko'rish»), ekotadbir ma'lumotlari.
 *     Joriy tilda tarjima bo'lmasa — bu haqda xabar beriladi.
 */
import { useEffect, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { ApiError } from "@/shared/api/client";
import { portalApi } from "@/shared/api/endpoints";
import type { PortalNewsItem } from "@/shared/api/types";
import { useI18n } from "@/shared/i18n";
import { formatDate } from "@/shared/lib/format";
import { SECTION_PATHS } from "@/shared/lib/sectionRoutes";
import { Icon } from "@/shared/ui/Icon";
import type { IconName } from "@/shared/ui/Icon";
import { Modal } from "@/shared/ui/Modal";

import { SectionPage } from "./SectionPages";
import { NewsCard, NewsSkeleton, toCard } from "./landing/news";
import { MediaGallery } from "./landing/MediaGallery";
import { SplitHead } from "./landing/SplitHead";

const PAGE_SIZE = 12;
const CATEGORIES = ["", "news", "event", "announcement"] as const;

interface ListState {
  phase: "loading" | "ready" | "error";
  items: PortalNewsItem[];
  count: number;
  page: number;
  pages: number;
  /** «Yana yuklash» bosilgan — mavjud ro'yxat ostida yuklanmoqda. */
  more: boolean;
}

export function NewsListPage() {
  const { t, lang } = useI18n();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const category = params.get("toifa") ?? "";
  const dateFrom = params.get("dan") ?? "";
  const dateTo = params.get("gacha") ?? "";
  const [text, setText] = useState(q);
  const [state, setState] = useState<ListState>({ phase: "loading", items: [], count: 0, page: 1, pages: 1, more: false });
  const [attempt, setAttempt] = useState(0);
  const [page, setPage] = useState(1);
  const filterKey = `${q}|${category}|${dateFrom}|${dateTo}|${lang}`;
  const lastKey = useRef("");

  useEffect(() => setText(q), [q]);

  useEffect(() => {
    /* Filtr o'zgarsa — birinchi sahifadan; «Yana yuklash» — keyingi sahifa qo'shiladi. */
    const reset = lastKey.current !== filterKey;
    lastKey.current = filterKey;
    const target = reset ? 1 : page;
    if (reset && page !== 1) {
      setPage(1);
      return undefined;
    }
    const controller = new AbortController();
    setState((previous) => ({ ...previous, phase: target === 1 ? "loading" : previous.phase, more: target > 1 }));
    portalApi
      .newsPage(
        { q, category, date_from: dateFrom, date_to: dateTo, page: target, page_size: PAGE_SIZE },
        lang,
        controller.signal,
      )
      .then((data) => {
        setState((previous) => ({
          phase: "ready",
          items: target === 1 ? data.results : [...previous.items, ...data.results],
          count: data.count,
          page: data.page,
          pages: data.pages,
          more: false,
        }));
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setState((previous) => ({ ...previous, phase: "error", more: false }));
      });
    return () => controller.abort();
  }, [filterKey, page, attempt, q, category, dateFrom, dateTo, lang]);

  function update(next: Record<string, string>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value) merged.set(key, value);
      else merged.delete(key);
    }
    setParams(merged, { replace: true });
  }

  function search(event: FormEvent) {
    event.preventDefault();
    update({ q: text.trim() });
  }

  const filtered = Boolean(q || category || dateFrom || dateTo);
  const cards = state.items.map((item) => toCard(item, t));

  return (
    <SectionPage revealKey={`${state.phase}-${state.items.length}`}>
      <section className="section" id="news-all" aria-labelledby="news-all-title">
        <div className="container">
          <SplitHead
            scene="news"
            eyebrow={t("nav.news")}
            title={t("news.title")}
            titleId="news-all-title"
            lead={t("news.lead")}
          />

          <form className="news-filters" role="search" onSubmit={search} data-reveal>
            <span className="field news-filter-search">
              <label className="visually-hidden" htmlFor="news-q">
                {t("news.search")}
              </label>
              <span className="input-group">
                <span className="input-icon">
                  <Icon name="search" size={16} />
                </span>
                <input
                  id="news-q"
                  className="input"
                  type="search"
                  placeholder={t("news.search")}
                  value={text}
                  onChange={(event) => setText(event.target.value)}
                  maxLength={100}
                />
              </span>
            </span>
            <button type="submit" className="btn btn-primary">
              {t("news.searchBtn")}
            </button>
            <div className="news-filter-cats segmented" role="group" aria-label={t("news.category")}>
              {CATEGORIES.map((code) => (
                <button
                  key={code || "all"}
                  type="button"
                  aria-pressed={category === code}
                  onClick={() => update({ toifa: code })}
                >
                  {t(code ? `news.cat.${code}` : "news.cat.all")}
                </button>
              ))}
            </div>
            <span className="field news-filter-date">
              <label className="label" htmlFor="news-from">
                {t("news.dateFrom")}
              </label>
              <input
                id="news-from"
                className="input"
                type="date"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(event) => update({ dan: event.target.value })}
              />
            </span>
            <span className="field news-filter-date">
              <label className="label" htmlFor="news-to">
                {t("news.dateTo")}
              </label>
              <input
                id="news-to"
                className="input"
                type="date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(event) => update({ gacha: event.target.value })}
              />
            </span>
            {filtered ? (
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setParams({}, { replace: true })}>
                <Icon name="refresh" size={14} />
                {t("news.reset")}
              </button>
            ) : null}
          </form>

          <p className="news-count" role="status" aria-live="polite">
            {state.phase === "loading"
              ? t("news.loading")
              : state.phase === "ready"
                ? t("news.found").replace("{n}", String(state.count))
                : ""}
          </p>

          {state.phase === "error" && !state.items.length ? (
            <NewsMessage icon="alert" tone="error" title={t("news.error")}>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => setAttempt((value) => value + 1)}>
                <Icon name="refresh" size={14} />
                {t("news.retry")}
              </button>
            </NewsMessage>
          ) : state.phase === "ready" && !state.items.length ? (
            <NewsMessage icon="search" title={t("news.empty")} text={filtered ? t("news.emptyHint") : ""}>
              {filtered ? (
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setParams({}, { replace: true })}>
                  {t("news.reset")}
                </button>
              ) : null}
            </NewsMessage>
          ) : (
            <div className="news-grid is-four" aria-busy={state.phase === "loading"}>
              {state.phase === "loading" ? (
                <NewsSkeleton count={8} />
              ) : (
                cards.map((item, index) => <NewsCard key={item.id} item={item} index={index} />)
              )}
              {state.more ? <NewsSkeleton count={4} /> : null}
            </div>
          )}

          {state.phase !== "loading" && state.items.length ? (
            <div className="news-more">
              <span className="hint">
                {t("news.shown").replace("{shown}", String(state.items.length)).replace("{total}", String(state.count))}
              </span>
              {state.phase === "error" ? (
                <p className="field-error" role="alert">
                  <Icon name="alert" size={14} />
                  {t("news.error")}
                </p>
              ) : null}
              {state.page < state.pages ? (
                <button
                  type="button"
                  className="btn btn-outline"
                  disabled={state.more}
                  onClick={() => (state.phase === "error" ? setAttempt((value) => value + 1) : setPage(state.page + 1))}
                >
                  {state.more ? <span className="gateway-spinner" aria-hidden="true" /> : <Icon name="chevronDown" size={16} />}
                  {state.phase === "error" ? t("news.retry") : t("news.more")}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </section>
      <MediaGallery embedded />
    </SectionPage>
  );
}

function NewsMessage({
  icon,
  title,
  text = "",
  tone = "",
  children,
}: {
  icon: IconName;
  title: string;
  text?: string;
  tone?: string;
  children?: ReactNode;
}) {
  return (
    <div className={`news-message${tone ? ` is-${tone}` : ""}`} role={tone === "error" ? "alert" : "status"}>
      <Icon name={icon} size={28} />
      <b>{title}</b>
      {text ? <p>{text}</p> : null}
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ maqola */

function timeOf(value: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(value));
}

export function NewsArticlePage() {
  const { id = "" } = useParams();
  const { t, lang, locale } = useI18n();
  const [item, setItem] = useState<PortalNewsItem | null>(null);
  const [phase, setPhase] = useState<"loading" | "ready" | "missing" | "error">("loading");
  const [attempt, setAttempt] = useState(0);
  const [photo, setPhoto] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPhase("loading");
    portalApi
      .newsItem(id, lang)
      .then((data) => {
        if (cancelled) return;
        setItem(data);
        setPhase("ready");
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setPhase(error instanceof ApiError && error.status === 404 ? "missing" : "error");
      });
    return () => {
      cancelled = true;
    };
  }, [id, lang, attempt]);

  useEffect(() => {
    if (item?.title) document.title = `${item.title} — Smart Forestry`;
  }, [item?.title]);

  const back = (
    <Link to={SECTION_PATHS.news} className="service-back">
      <Icon name="chevronRight" size={14} /> {t("news.all")}
    </Link>
  );

  if (phase !== "ready" || !item) {
    return (
      <SectionPage revealKey={phase}>
        <section className="section" aria-busy={phase === "loading"}>
          <div className="container news-article-wrap">
            <div className="news-article-back">{back}</div>
            {phase === "loading" ? (
              <div className="news-article news-skeleton" aria-hidden="true">
                <span className="sk-line is-short" />
                <span className="sk-line is-title" />
                <span className="sk-block" />
                <span className="sk-line" />
                <span className="sk-line" />
              </div>
            ) : phase === "missing" ? (
              <NewsMessage icon="search" title={t("news.notFound")}>
                <Link className="btn btn-outline btn-sm" to={SECTION_PATHS.news}>
                  {t("news.all")}
                </Link>
              </NewsMessage>
            ) : (
              <NewsMessage icon="alert" tone="error" title={t("news.error")}>
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setAttempt((value) => value + 1)}>
                  <Icon name="refresh" size={14} />
                  {t("news.retry")}
                </button>
              </NewsMessage>
            )}
          </div>
        </section>
      </SectionPage>
    );
  }

  const category = item.category ?? "news";
  const translated = !item.translations?.length || item.translations.includes(lang);
  /* gov.uz da maqola suratlari (slayder) muqovaning kattaroq nusxasi — ular bo'lsa birinchisi
     asosiy surat bo'ladi, qolganlari galereyaga tushadi (bir surat ikki marta chiqmaydi). */
  const visuals = (item.images ?? []).filter(Boolean);
  const mainImage = visuals[0] ?? item.cover_link;
  const gallery = visuals.slice(1);
  const isEvent = category === "event" || Boolean(item.event_starts_at);

  return (
    <SectionPage revealKey={String(item.id)}>
      <section className="section" aria-labelledby="news-article-title">
        <div className="container news-article-wrap">
          <div className="news-article-back">{back}</div>
          <article className="news-article">
            <header className="news-article-head">
              <div className="news-badges">
                <span className={`badge news-cat is-${category}`}>{t(`news.kind.${category}`)}</span>
                {item.is_sample ? <span className="badge news-sample">{t("news.sample")}</span> : null}
              </div>
              <h1 id="news-article-title">{item.title}</h1>
              <p className="news-article-meta">
                <span>
                  <Icon name="calendar" size={14} />
                  {t("news.published")}: <time dateTime={item.published_at}>{formatDate(item.published_at, locale)}</time>
                </span>
                {item.view_count ? (
                  <span>
                    <Icon name="eye" size={14} />
                    {t("news.views")}: <span className="num">{item.view_count}</span>
                  </span>
                ) : null}
              </p>
            </header>

            {!translated ? (
              <p className="alert alert-info" role="note">
                <Icon name="info" size={18} />
                <span>{t("news.noTranslation")}</span>
              </p>
            ) : null}

            {isEvent ? (
              <section className="news-event" aria-label={t("news.event")}>
                <h2>{t("news.event")}</h2>
                <dl>
                  {item.event_starts_at ? (
                    <div>
                      <dt>{t("news.eventDate")}</dt>
                      <dd>
                        {formatDate(item.event_starts_at, locale)}
                        {item.event_ends_at && formatDate(item.event_ends_at, locale) !== formatDate(item.event_starts_at, locale)
                          ? ` — ${formatDate(item.event_ends_at, locale)}`
                          : ""}
                      </dd>
                    </div>
                  ) : null}
                  {item.event_starts_at ? (
                    <div>
                      <dt>{t("news.eventTime")}</dt>
                      <dd className="num">
                        {timeOf(item.event_starts_at, locale)}
                        {item.event_ends_at ? ` — ${timeOf(item.event_ends_at, locale)}` : ""}
                      </dd>
                    </div>
                  ) : null}
                  {item.event_place ? (
                    <div>
                      <dt>{t("news.eventPlace")}</dt>
                      <dd>{item.event_place}</dd>
                    </div>
                  ) : null}
                  {item.event_organizer ? (
                    <div>
                      <dt>{t("news.eventOrganizer")}</dt>
                      <dd>{item.event_organizer}</dd>
                    </div>
                  ) : null}
                  {item.event_participation ? (
                    <div>
                      <dt>{t("news.eventParticipation")}</dt>
                      <dd>{item.event_participation}</dd>
                    </div>
                  ) : null}
                  <div>
                    <dt>{t("news.published")}</dt>
                    <dd>{formatDate(item.published_at, locale)}</dd>
                  </div>
                </dl>
              </section>
            ) : null}

            {mainImage ? (
              <figure className="news-article-cover">
                <img src={mainImage} alt="" />
              </figure>
            ) : null}

            {item.body ? (
              /* Matn backendda tozalanadi (`sanitize_html`: script, on* atributlar, javascript:). */
              <div className="news-article-body" dangerouslySetInnerHTML={{ __html: item.body }} />
            ) : item.summary ? (
              <p className="news-article-body">{item.summary}</p>
            ) : null}

            {gallery.length ? (
              <section className="news-gallery" aria-label={t("news.gallery")}>
                <h2>{t("news.gallery")}</h2>
                <div className="news-gallery-grid">
                  {gallery.map((url, index) => (
                    <button key={url} type="button" onClick={() => setPhoto(url)} aria-label={`${t("news.gallery")} ${index + 1}`}>
                      <img src={url} alt="" loading="lazy" />
                    </button>
                  ))}
                </div>
              </section>
            ) : null}

            <footer className="news-source">
              <span>
                <Icon name="building" size={16} />
                {t("news.source")}: <b>{item.source_label || "O'rmon agentligi"}</b>
              </span>
              {item.source_url ? (
                <a className="btn btn-outline btn-sm" href={item.source_url} target="_blank" rel="noreferrer noopener">
                  {t("news.official")}
                  <Icon name="external" size={14} />
                </a>
              ) : null}
            </footer>
          </article>
        </div>
      </section>

      <Modal open={Boolean(photo)} title={t("news.gallery")} onClose={() => setPhoto(null)} size="xl">
        {photo ? <img className="news-photo-full" src={photo} alt="" /> : null}
      </Modal>
    </SectionPage>
  );
}
